import { A, all, Div, reactive, Section, Span, Variable, VBoolean, VArray } from 'alkali'
import Icon from 'components/Icon'
import * as controlIcon from 'icons/pill.svg'
import { uniq } from 'lodash'
import { selectedOutcomes } from 'model/EvidenceSearch'
import { analysisSelections, faqOrReadOnly, analysisType } from 'model/SearchQuery'
import { numToString, getCIWidth } from 'model/AnalyzeStudy'
import { estimateValuesFromAMs } from 'analysis/Effect'
import { renderInitial } from '../filter/InterventionDropdown'
import { getUnitType, isBinary } from 'analysis/RowTypeUnit'
import { userSettings } from 'app/app'

const countUnitCheck = (unitValue: number, unitType: string) => {
	if (unitType.toLowerCase().includes('count')) {
		return (unitValue > 1) ? unitType.replace('Count', 'events') : unitType.replace('Count', 'event')
	}
	return unitType
}
export class StudyGroups extends Div('.study-body-groups') {
	created(properties) {
		let {
			groups,
			outcome,
			getOutcomeValue,
			groupAnalyzeAs,
			groupHover, groupHoverClose,
			effectElement,
			treatmentCircle,
		} = properties
		let lastOutcome, lastGroups
		properties.content = reactive(groups).to(groups => {
			if (outcome.get) {
				if (lastOutcome == outcome.get('name'))
					groups = lastGroups // preserve sort order
				else {
					lastOutcome = outcome.get('name')
					lastGroups = groups
				}
			}
			let teGroups = new Set()
			if (!groups)
				return []
			for (let group of groups) {
				if (group.analyzeAs) {
					teGroups.add(group.analyzeAs)
					if (teGroups.size >= 2)
						break
				}
			}
			return groups.map((group, groupIndex) => {
			let { N: groupN } = group
			const name = group.Name || group.OriginalName
			let groupTitle = [name]
			if (group.description)
				groupTitle.push(group.description)
				if (group.additionalInformation)
					groupTitle.push(group.additionalInformation)
			let firstIntervention = true
			for (let intervention of group.Interventions || []) {
				if (intervention.name) {
					if (firstIntervention) {
						groupTitle.push('Interventions:')
						firstIntervention = false
					}
					groupTitle.push(intervention.name)
				}
			}
			let analyzeAs = group.analyzeAs
			let unitType = getUnitType(outcome.valueOf())
			let selectedOutcome = selectedOutcomes.valueOf()?.[0] // TODO: Need to get loop through these in multiple outcome scenarios
			estimateValuesFromAMs(outcome.valueOf(), outcome.valueOf().minMax || selectedOutcome?.ranges?.[unitType])
			let outcomeValue = getOutcomeValue(group)
			let outcomePosition = outcomeValue.to((outcomeValue) => {
				// find the master outcome to bring the min-max over
				if (!outcome.valueOf()) return {}
				let outcomeId = outcome.valueOf().id
				let unitType = getUnitType(outcome.valueOf())
				let differentUnitType = getUnitType(selectedOutcome) != unitType
				let minMax = outcome.minMax || selectedOutcome?.ranges?.[unitType]

				if (!minMax) {
					let min = Infinity
					let max = -Infinity
					for (let cell of outcome.valueOf().cells || []) {
						if (cell.number < min)
							min = cell.number
						if (cell.number > max)
							max = cell.number
					}
					if (min > -Infinity) {
						let range = max - min
						minMax = {
							min: min > 0 ? 0 : min - range / 3,
							max: max + range / 3
						}
					} else {
						return {
							has0Axis: false,
							negativeFlex: 0,
							positiveFlex: 1,
							value: 0,
							isPositive: true,
						}
					}
				}
				if (selectedOutcome?.type == 'Binary')
					minMax.min = Math.min(minMax.min, 0)
				if (outcomeValue?.estimated)
					outcomeValue = outcomeValue.estimated
				let lower, upper
				if (typeof outcomeValue?.value == 'number') {
					lower = outcomeValue.lower
					upper = outcomeValue.upper
					outcomeValue = outcomeValue.value
				}
				let { min, max } = minMax
				let range = max - min
				let has0Axis = min <= 0 && max >= 0
				let value, negativeFlex, positiveFlex
				let isOutlier = outcomeValue > minMax.outlierMax || outcomeValue < minMax.outlierMin
				let isPositive = outcomeValue >= 0
				if (has0Axis) {
					value =
						outcomeValue > 0
							? outcomeValue / Math.max(max, 0.0001)
							: outcomeValue / Math.min(min, -0.0001)
					negativeFlex = Math.max(-min, 0) / range
					positiveFlex = Math.max(max, 0) / range
					if (value > 1)
						value = 1
					if (value < 0)
						value = 0
					if (min < 0) {
						if (!isPositive) {
							value *= negativeFlex / (negativeFlex + 0.125)
						}
						negativeFlex += 0.125
						min = (min / range - 0.125) * range
					}
					if (max > 0) {
						if (isPositive) {
							value *= positiveFlex / (positiveFlex + 0.125)
						}
						positiveFlex += 0.125
						max = (max / range + 0.125) * range
					}
				} else {
					if (max <= 0) {
						value = (max - outcomeValue) / range
						negativeFlex = 1
						positiveFlex = 0
						isPositive = false
					} else {
						value = (outcomeValue - min) / range
						negativeFlex = 0
						positiveFlex = 1
						isPositive = true
					}
					if (value > 0.875)
						value = 0.875
					if (value < 0.02)
						value = 0.02
				}
				range = max - min
				return {
					has0Axis,
					value: value * 100,
					lower: (lower - min) / range * 100,
					upper: (upper - min) / range * 100,
					negativeFlex,
					positiveFlex,
					isPositive,
					isOutlier,
					differentUnitType,
				}
			})
			let outcomeValueWithUnit = all([outcome, outcomeValue], (outcome, value) => {
				if (typeof value?.value == 'number')
					value = value.value
				if (typeof value != 'number' || isNaN(value)) return ''
				let unit =
					outcome.type == 'Binary'
						? '%'
						: outcome.unit
						? outcome.unit.length < 4
							? outcome.unit
							: ' ' + countUnitCheck(numToString(value), outcome.unit.toString())
						: ''
				return numToString(value) + unit
			})
			let confidenceLevel = analysisSelections.get('settings')?.confidenceLevel || 95
			let outcomeHasAMs = (outcome.to ? outcome : reactive(outcome)).to(outcome => outcome?.cells?.find(cell => cell.AMs && cell.group == group.Id))
			const getValueDescription = () => {
				let cellData
				let cellCount
				let participantNumerator
				let outcomeValueOf = outcome.valueOf()
				if (!outcomeValueOf)
					return false
				//  What to display for participant fraction
				let participantFraction
				// Check for binary or percent unit - set to and rather than or as % let % change studyies pass
				let binaryOrPercentCheckOF = isBinary(outcomeValueOf)
				let cells = outcomeValueOf.cells
				// Match intervention data from outcome to intervention group
				if (!group.Id) {
					let N = group?.N
					if (analysisType == 'cohort')
						return `Estimated ${outcomeValueOf.unit} from cohort analysis`
					return `Estimated ${outcomeValueOf.unit} based on extrapolating from ${group.analyzeAs == 'Control' ? '' : 'the effect size and '}median absolute effects.`
				}
				cellData = group.Id ? cells?.find((cell) => cell.group === group.Id) : group
				let N = cellData?.N || group?.N
				// If there are interventions and it passes binary check
				if (cellData && N) {
					if (binaryOrPercentCheckOF) {
						cellCount = cellData.count ?? (cellData.number && Math.round((cellData.number * N) / 100))
						participantFraction = cellCount == null ? `${N} participants (no count reported${cellData.AMs ? ', graph estimated from treatment effect' : ''})` :
							`${cellCount} of the ${N} participants`
						return participantFraction
					} else {
						let type = outcomeValueOf.dataType
						if ((type == 'Integer' || type == 'Event') && cellData.count && outcomeValueOf.time) {
							return cellData.count + ` events out of ${N} participants over ` + outcomeValueOf.time.description
						}
						let valueWithUnit = outcomeValueWithUnit.valueOf()
						let ciWidth = cellData.se * getCIWidth()
						let number = cellData.number
						if (number == null && cellData.AMs) {
							return 'Graph estimated from treatment effect'
						}
						const getCI = () => isNaN(ciWidth) ?
							'(no variance provided)' :
							('(' + numToString(number - ciWidth) + ' to ' + numToString(number + ciWidth) + ', ' + confidenceLevel + '% CI)')
						if (outcomeValueOf.type == 'Change' || outcomeValueOf.type == '% Change') {
							if (outcomeValue < 0) {
								number = -number
								return `${outcomeValueWithUnit.valueOf().replace(/^-/,'')} ${getCI()} lower based on ${N} participants`
							} else {
								return `${outcomeValueWithUnit} ${getCI()} higher based on ${N} participants`
							}
						} else {
							return `${outcomeValueOf.dataType ? outcomeValueOf.dataType + ' of ' : ''
								}${outcomeValueWithUnit} ${getCI()} based on ${N} participants`
							}
					}
				}
			}
			let pscoreDescription = group.pscore && group.pscore != 0.5 ? ` ${group.analyzeAs || group.name} has a ${Math.round(group.pscore * 100)}% probability of superiority to any of the other treatments based on this analysis.` : ''
			let ValueDiv =
				Div('.study-outcome-value', {
					dataset: {
						outcome: outcomeValueWithUnit,
					},
					title: outcomePosition.property('isOutlier').to((isOutlier) =>
						(getValueDescription() || outcomeValueWithUnit) +
							(isOutlier ? ' (this value is an outlier)' : '') +
							pscoreDescription),
					style: {
						'--outcome-width': outcomePosition.to(({ value }) => value + '%'),
					}
				})

			return Div(
				'.study-group',
				{
					classes: {
						'group-selected': analyzeAs,
						'baseline-group': group.isBaseline,
						'has-parent-group': Boolean(group.ParentGroup),
					},
					onclick: (event) => {
						if (properties.selectGroup && !faqOrReadOnly.valueOf())
							properties.selectGroup(event, group)
					},
				},
				[
					Div('.selector-checkbox', {
						classes: {
							'selector-selected': analyzeAs,
							'selector-disabled': outcomeValueWithUnit.to((value) => !value && !outcomeHasAMs.valueOf()),
						},
						title: outcomeValueWithUnit.to((value) =>
							value
								? (analyzeAs ?
									'Analyzed as intervention group ' + analyzeAs + '. ' 
									: '') +
									((!faqOrReadOnly.valueOf()) ? 'Click to select if and how this treatment should be compared' : '').trim()
								: ((!faqOrReadOnly.valueOf()) ?
								'No measurable outcome value provided, this treatment can not be selected for comparison' : '').trim()
						),
						onclick: (event) => {
							if (!faqOrReadOnly.valueOf()) {
								event.stopPropagation()
								properties.groupMenu(event, group, outcomeValueWithUnit.valueOf() || outcomeHasAMs.valueOf())
							}
						},
						content: renderInitial(analyzeAs, true),
					}),
					Div('.study-group-name', {
						title: groupHover ? '' : groupTitle.join('\n') + (group.isBaseline ? ' (analyzed as baseline)' : ''),
						content: name,
						onmouseover(event) {
							if (groupHover)
								groupHover(group, event, this)
						},
						onmouseout(event) {
							if (groupHoverClose)
								groupHoverClose(event)
						}
					}),
					Div('.study-outcome-container', [
						Div('.study-outcome.study-outcome-negative', {
							classes: {
								outlier: outcomePosition.property('isOutlier'),
								'different-unit-type': outcomePosition.property('differentUnitType'),
								'has-0-axis': outcomePosition.property('has0Axis'),
							},
							flex: outcomePosition.to(({ negativeFlex }) => negativeFlex + ' 0 0'),
							content: !outcomePosition.get('isPositive') ? ValueDiv : null,
						}),
						Div('.study-outcome.study-outcome-positive', {
							classes: {
								outlier: outcomePosition.property('isOutlier'),
								'different-unit-type': outcomePosition.property('differentUnitType'),

							},
							flex: outcomePosition.to(({ positiveFlex }) => positiveFlex + ' 0 0'),
							content: outcomePosition.get('isPositive') ? ValueDiv : null,
						}),
						Div('.confidence-interval-bar-clipper', [
							Div('.confidence-interval-bar', {
								display: outcomePosition.to(({ lower }) => !isNaN(lower) && !userSettings.get('HideErrorBars')),
								left: outcomePosition.to(({ lower }) => lower + '%'),
								right: outcomePosition.to(({ upper }) => (100 - upper) + '%'),
							})
						]),
					]),
					effectElement
						? effectElement(group)
						: (treatmentCircle && teGroups.has(analyzeAs))
						? groupIndex < 2
							? Div('.te-circle-connector', {display: treatmentCircle})
							: Div('.te-circle-long-connector', [
									Div('.te-circle-vertical-connector', {
										height: groupIndex * 2.2 - 3.1 + 'em',
									}),
							  ], {display: treatmentCircle})
						: null,
				]
			)
		})
	})
	}
}
